import { Group, NumberInput } from "@mantine/core"
import { ListTableActionIconGroup } from "../../-components/list-table-action-icon-group"
import { useGetResourcePermission } from "@/orval/gen"
import { useUserLocalStorage } from "../../-components/use-user-local-storage"
import { Page } from "../../../../-components/Page"
import { ResourceTypeCategory } from "../../../../@client/Theasoft_DataClasses_Dispo"
import { ResourceTypeCategorySelect } from "./ResourceTypeCategorySelect"
import { useNavigate, useSearch } from "@tanstack/react-router"
import { ResourceObjectsSegmentControl } from "./ResourceObjectsSegmentControl"
import {
  getPaginatedResources,
  ResourceDto,
  useGetResourceFieldTypes,
  useGetResourceTypeCategoriesSuspense,
  useGetSimpleOwnerGroupsSuspense,
} from "@/orval/gen"
import { ResourcesTable } from "./ResourcesTable"
import {
  AddFieldFilterModal,
  FieldFilterButton,
  FieldFilterPill,
  getUpdatedFilterDict,
} from "@components/field-data"
import {
  ResponsiveFilterGroup,
  useResponsiveFilterQueryOptions,
} from "../../-components/hideable-filter-group"
import { SearchTextInput } from "../../-components/search-text-input"
import { ResourceTypeTreeSelect } from "./ResourceTypeTreeSelect"
import { useTranslation } from "react-i18next"
import { useDisclosure } from "@mantine/hooks"
import { Button } from "@mantine/core"
import { CreateResourceModal } from "./CreateResourceModal"
import { useDefaultInfiniteQuery } from "@/utils/useDefaultInfiniteQuery"
import { ResourcesCardList } from "./ResourcesCardList"
import { SortingStateAndUpdater } from "@/routes/-components/custom-tanstack-table"
import { ErrorAlert } from "@/blocks/errors/error-alert"

export function ResourcesPage() {
  const [storedView, setStoredView] = useUserLocalStorage<"table" | "list">({
    key: "resources-page-view",
    defaultValue: "list",
  })
  const { t } = useTranslation("app_server")
  const { data: categories } = useGetResourceTypeCategoriesSuspense()
  const { data: ownerGroups } = useGetSimpleOwnerGroupsSuspense()
  const defaultOwnerGroupId = ownerGroups?.[0]?.ownerGroupId ?? 0
  const { data: permission } = useGetResourcePermission({
    ownerGroupId: defaultOwnerGroupId,
  })
  const canWriteResources = permission === "Write"
  const [visible, setVisible] = useUserLocalStorage<boolean>({
    key: "resources-filter-visibility",
    defaultValue: true,
  })
  const queryOptions = useResponsiveFilterQueryOptions(visible)
  const [storedCategory, setStoredCategory] =
    useUserLocalStorage<ResourceTypeCategory>({
      key: "resource-category",
      defaultValue: categories[0],
    })
  const search = useSearch({ from: "/app/resources/resources" })
  const navigate = useNavigate()
  const navigateToObjects = () =>
    navigate({
      to: "/app/resources/resource-objects",
      search: { available: true },
    })
  const { data: fieldTypes } = useGetResourceFieldTypes(
    {
      fieldVisibility: "Resource",
      category: storedCategory,
      previewOnly: true,
    },
    { query: { initialData: [] } }
  )
  const [modalOpened, { open, close }] = useDisclosure()
  const [createModalOpened, { open: openCreate, close: closeCreate }] =
    useDisclosure()

  const updateSearch = (newValue: Partial<typeof search>) => {
    navigate({
      to: "/app/resources/resources",
      search: { ...search, ...newValue },
      replace: true,
    })
  }

  const query = useDefaultInfiniteQuery(
    getPaginatedResources,
    (pageRequest) => ({
      ...pageRequest,
      ...search,
      sorting: storedView === "table" ? search.sorting : undefined,
    }),
    queryOptions
  )

  const onClickItem = (item: ResourceDto) => {
    navigate({
      to: "/app/resources/$id",
      params: { id: item.resourceId.toString() },
    })
  }

  const sortingState: SortingStateAndUpdater = [
    search.sorting ?? [],
    (updater) => {
      const newSorting =
        typeof updater === "function" ? updater(search.sorting ?? []) : updater
      updateSearch({ sorting: newSorting })
    },
  ]

  const handleCategoryChange = (newCategory: ResourceTypeCategory) => {
    updateSearch({ resourceTypeTreeNodes: undefined, category: newCategory })
  }

  React.useEffect(() => {
    if (search.category && storedCategory !== search.category) {
      setStoredCategory(search.category)
    }
  }, [storedCategory, search.category, setStoredCategory])

  const category = search.category

  if (!category) {
    if (storedCategory) {
      updateSearch({ category: storedCategory })
      return null
    }
    if (categories.length === 0) return <ErrorAlert error={t("NothingFound")} />
    updateSearch({ category: categories[0] })
    return null
  }

  return (
    <Page
      crumbOverride={() => (
        <Group>
          <ResourceTypeCategorySelect
            value={category}
            onChange={handleCategoryChange}
          />
          <ResourceObjectsSegmentControl
            category={category}
            value={"Resources"}
            onChange={navigateToObjects}
          />
        </Group>
      )}
      buttonGroupProps={{
        children: (
          <Group>
            {canWriteResources && (
              <Button onClick={openCreate}>{t("New")}</Button>
            )}
            <ListTableActionIconGroup
              value={storedView}
              onChange={(value) => {
                setStoredView(value)
              }}
            />
          </Group>
        ),
      }}
      titleGroupProps={{ align: "start" }}
    >
      <Group pb={5} justify="space-between">
        <Group>
          <SearchTextInput
            autoFocus
            value={search.globalFilter ?? undefined}
            onChange={(e) => updateSearch({ globalFilter: e.target.value })}
            placeholder={`${t("Search")} in <${t(`ResourceTypeCategory_${category}`)}>`}
          />
          <ResponsiveFilterGroup
            align="end"
            visible={visible}
            setVisible={setVisible}
            filters={{
              resourceTypes: search.resourceTypeTreeNodes,
              availableCount: search.availableCount,
              ...Object.fromEntries(
                Object.entries(search.fieldFilter ?? {}).map(([id, filter]) => [
                  `field-${id}`,
                  filter,
                ])
              ),
            }}
            onReset={() =>
              updateSearch({
                resourceTypeTreeNodes: undefined,
                availableCount: undefined,
                fieldFilter: undefined,
              })
            }
          >
            <ResourceTypeTreeSelect
              category={storedCategory}
              miw={250}
              label={t("Type")}
              onCheckedStateChange={(values) =>
                updateSearch({ resourceTypeTreeNodes: values })
              }
              initialCheckedState={search.resourceTypeTreeNodes ?? []}
            />
            <NumberInput
              label={`${t("Available")}/${t("Amount")}`}
              value={search.availableCount ?? undefined}
              onChange={(value) =>
                typeof value === "number"
                  ? updateSearch({ availableCount: value })
                  : updateSearch({ availableCount: undefined })
              }
            />
            {fieldTypes.length > 0 && <FieldFilterButton onClick={open} />}
            {fieldTypes?.map(
              (type) =>
                !!search.fieldFilter?.[type.id] && (
                  <FieldFilterPill
                    typeName={type.name}
                    filter={search.fieldFilter[type.id]}
                    onRemove={() => {
                      if (search.fieldFilter) {
                        const { [type.id]: _, ...rest } = search.fieldFilter

                        updateSearch({ fieldFilter: rest })
                      }
                    }}
                  />
                )
            )}
          </ResponsiveFilterGroup>
        </Group>
        <ResponsiveFilterGroup.Button
          visible={visible}
          onChange={setVisible}
          filters={{
            resourceTypes: search.resourceTypeTreeNodes,
            availableCount: search.availableCount,
            ...Object.fromEntries(
              Object.entries(search.fieldFilter ?? {}).map(([id, filter]) => [
                `field-${id}`,
                filter,
              ])
            ),
          }}
        />
      </Group>
      {storedView === "list" ? (
        <ResourcesCardList queryResult={query} onClickItem={onClickItem} />
      ) : (
        <ResourcesTable
          queryResult={query}
          onClickItem={onClickItem}
          sortingState={sortingState}
          fieldTypes={fieldTypes}
        />
      )}
      <CreateResourceModal
        opened={createModalOpened}
        onClose={closeCreate}
        defaultCategory={storedCategory}
      />
      <AddFieldFilterModal
        opened={modalOpened}
        onClose={close}
        fieldTypes={fieldTypes}
        onAdd={(record) => {
          updateSearch({
            fieldFilter: getUpdatedFilterDict(
              record.type,
              record.value,
              search.fieldFilter
            ),
          })
        }}
        values={search.fieldFilter}
      />
    </Page>
  )
}
